/**
 * Linguagem do contexto products — o catálogo comercial do grupo.
 *
 * Os produtos vêm da API (`/v1/products/public`) e são gerenciados no
 * backoffice; `infrastructure/api.ts` traduz o contrato para estes tipos.
 */

/** As marcas do grupo, na ordem do menu. */
export const Brand = {
  NUTRITION: 'central-nutrition',
  FARMA: 'central-farma',
  INJETAVEIS: 'central-injetaveis',
} as const;
export type Brand = (typeof Brand)[keyof typeof Brand];

export const BRANDS: { id: Brand; label: string }[] = [
  { id: Brand.NUTRITION, label: 'Central Nutrition' },
  { id: Brand.FARMA, label: 'Central Farma' },
  { id: Brand.INJETAVEIS, label: 'Central Injetáveis' },
];

export function isBrand(value: unknown): value is Brand {
  return BRANDS.some((b) => b.id === value);
}

/** Forma da embalagem — decide a ilustração enquanto não há foto. */
export type Packaging = 'pot' | 'bottle' | 'vial' | 'box';

/** Um trecho de texto com título opcional — "Recomendações", "Advertência"… */
export interface InfoBlock {
  title?: string;
  paragraphs: string[];
}

/** O conteúdo das abas do painel de detalhe. */
export interface ProductDetails {
  description: InfoBlock[];
  composition: { ingredient: string; amount?: string }[];
  usage: InfoBlock[];
}

export interface Product {
  id: string;
  slug: string;
  name: string;
  summary: string;
  brand: Brand;
  packaging: Packaging;
  bestSeller: boolean;
  imageUrl?: string;
  details?: ProductDetails;
}

export type DetailTab = 'description' | 'composition' | 'usage';

export const DETAIL_TABS: { id: DetailTab; label: string }[] = [
  { id: 'description', label: 'Descrição' },
  { id: 'composition', label: 'Composição' },
  { id: 'usage', label: 'Modo de usar' },
];

/** A aba tem algo para mostrar? Aba vazia mostra aviso, não some — o layout não pula. */
export function hasContent(details: ProductDetails | undefined, tab: DetailTab): boolean {
  if (!details) return false;
  return details[tab].length > 0;
}

export interface ProductFilter {
  brand?: Brand;
  /** Só os favoritos do usuário. */
  favoriteIds?: readonly string[];
}

/** Mais vendidos primeiro; dentro do grupo, ordem alfabética. */
export function filterProducts(products: readonly Product[], filter: ProductFilter): Product[] {
  return products
    .filter((p) => !filter.brand || p.brand === filter.brand)
    .filter((p) => !filter.favoriteIds || filter.favoriteIds.includes(p.id))
    .sort((a, b) => Number(b.bestSeller) - Number(a.bestSeller) || a.name.localeCompare(b.name, 'pt-BR'));
}
